class Hero extends HTMLElement {
  constructor() {
    super();
    this.parent = null;
    this.parentHeight = null;
    this.options = {};
    this.placedPaths = [];
  }

  connectedCallback() {
    this.parent = this;
    this.options = JSON.parse(this.parent.getAttribute("data-options"));
    this.setComponentHeight();

    window.addEventListener(
      "resize",
      debounce(() => this.setComponentHeight(), 200)
    );

    if (
      this.parent &&
      this.options?.lettersAnimationType &&
      this.options?.borderAnimationType
    ) {
      switch (this.options?.lettersAnimationType) {
        case "none":
          this.handleNone();
          break;
        case "reveal":
          this.handleReveal();
          break;
        case "scatter":
          this.handleScatter();
          break;
        case "scatter-reverse":
          this.handleScatterReverse();
          break;
        default:
          console.error("No animation type was specified.");
          break;
      }
    } else {
      console.error("Parent element is undefined.");
    }
  }

  debounce(func, delay) {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }

  setComponentHeight() {
    const header = document.querySelector("header.header");
    if (header) {
      let offsetHeight = window.innerHeight - header.clientHeight;
      this.parent.setAttribute("style", `--offset-height: ${offsetHeight}px`);
    }
  }

  shuffle(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  handleNone() {
    const paths = Array.from(this.parent.querySelectorAll("svg > path"));

    paths.forEach((path) => {
      path.style.transition = "none";
      path.style.opacity = "1";
    });
  }

  handleReveal() {
    const paths = Array.from(this.parent.querySelectorAll("svg > path"));
    const borderPath = paths.find((path) =>
      path.classList.contains("logo--border")
    );
    const otherPaths = this.shuffle(
      paths.filter((path) => !path.classList.contains("logo--border"))
    );

    const growBorder = this.parent.querySelector(".grow--border");
    const growPaths = this.parent.querySelectorAll(".grow--border > *");

    otherPaths.forEach((path, index) => {
      setTimeout(() => {
        path.style.opacity = "1";
      }, index * 400); // Adjust the timing as needed
    });

    if (borderPath) {
      if (this.options?.borderAnimationType === "grow")
        growBorder.style.opacity = "1";

      let delay = 0;
      switch (this.options?.borderAnimationType) {
        case "reveal":
          delay = (otherPaths.length * 400) / 1000;
          borderPath.setAttribute(
            "style",
            `opacity: 1; transition: opacity 1.2s ${delay}s ease-in-out;`
          );
          break;
        case "grow":
          growPaths.forEach((element, index) => {
            element.setAttribute("style", "--width: 100%");

            if (index >= growPaths.length - 2) {
              element.setAttribute("style", "--width: 30%");
            }
          });
          break;
        default:
          console.error("No border animation type was specified.");
          break;
      }
    }
  }

  handleScatter() {
    const parentWidth = this.parent.clientWidth;
    const parentHeight = this.parent.clientHeight;
    const paths = Array.from(this.parent.querySelectorAll("svg > path"));
    const otherPaths = paths.filter(
      (path) => !path.classList.contains("logo--border")
    );
    const shuffledPaths = this.shuffle(otherPaths);

    shuffledPaths.forEach((path, index) => {
      const position = this.findNonOverlappingPosition(
        parentWidth,
        parentHeight
      );

      path.style.opacity = "1";

      setTimeout(() => {
        path.style.transform = `translate(${position.x}px, ${position.y}px)`;
        path.style.transition = "all 1.2s 2.4s ease-in-out";
      }, index * 400); // Adjust the timing as needed

      this.placedPaths.push({ element: path, ...position });
    });
  }

  handleScatterReverse() {
    const parentWidth = this.parent.clientWidth;
    const parentHeight = this.parent.clientHeight;
    const paths = Array.from(this.parent.querySelectorAll("svg > path"));
    const borderPath = paths.find((path) =>
      path.classList.contains("logo--border")
    );
    const otherPaths = paths.filter(
      (path) => !path.classList.contains("logo--border")
    );
    const shuffledPaths = this.shuffle(otherPaths);

    const growBorder = this.parent.querySelector(".grow--border");
    const growPaths = this.parent.querySelectorAll(".grow--border > *");

    shuffledPaths.forEach((path, index) => {
      const position = this.findNonOverlappingPosition(
        parentWidth,
        parentHeight
      );

      path.style.transform = `translate(${position.x}px, ${position.y}px)`;

      setTimeout(() => {
        path.style.opacity = "1";
        path.style.transform = `translate(0, 0)`;
        path.style.transition = "all 1.2s ease-in-out";
      }, index * 400); // Adjust the timing as needed

      this.placedPaths.push({ element: path, ...position });
    });

    // Animate the logo border path last
    if (borderPath) {
      if (this.options?.borderAnimationType === "grow")
        growBorder.style.opacity = "1";

      setTimeout(() => {
        switch (this.options?.borderAnimationType) {
          case "reveal":
            borderPath.style.opacity = "1";
            break;
          case "grow":
            growPaths.forEach((element, index) => {
              element.setAttribute("style", "--width: 100%");

              if (index >= growPaths.length - 2) {
                element.setAttribute("style", "--width: 30%");
              }
            });
            break;
          default:
            console.error("No border animation type was specified.");
            break;
        }
      }, shuffledPaths.length * 400); // Delay should be equal to the total time taken by the other paths
    }
  }

  findNonOverlappingPosition(width, height) {
    const maxAttempts = 100;
    let attempts = 0;
    let position = null;

    while (attempts < maxAttempts) {
      const randomX = Math.random() * width - width / 2;
      const randomY = Math.random() * height - height / 2;
      const newPosition = { x: randomX, y: randomY };

      if (this.isNonOverlapping(newPosition)) {
        position = newPosition;
        break;
      }

      attempts++;
    }

    return position || { x: 0, y: 0 }; // Fallback to (0, 0) if no non-overlapping position is found
  }

  isNonOverlapping(newPosition) {
    const padding = 20; // Adjust padding as needed

    return this.placedPaths.every(({ x, y }) => {
      const distance = Math.sqrt(
        (x - newPosition.x) ** 2 + (y - newPosition.y) ** 2
      );
      return distance > padding;
    });
  }
}

customElements.define("component-hero", Hero);
